import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AccountContext, initialSignRequest } from "Context";
import { FolderItem, Folder, Path } from "./interface";
import {
  createNewFolder,
  deleteAnywayFolder,
  deleteTemplate,
  fetchFolderData,
  fetchFolderDataCount,
  fetchFolderTree,
  moveFolder,
  renameFolder,
  searchTemplatesAndFolders,
} from "./MyTemplatesService";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import { AccountDetails, SignRequest, SortingConfig } from "models/interfaces";
import { fetchAccountDetails } from "Services/CommonServices";
import TemplateHeader from "./components/TemplateHeader";
import {
  renderGridSkeletons,
  renderListSkeletons,
  renderMobileSkeletons,
} from "./MyTemplateHelper";
import { CustomDialogComponent } from "./MyTemplateHelper";
import { TemplateGridView } from "./components/TemplateGridView";
import { ItemListRow } from "./components/TemplateListView";
import { TemplateItemMenu } from "./components/TemplateItemMenu";
import TemplatesToolbar from "./components/TemplatesToolbar";
import templatesStyles from "./Templates.styles";
import templatesTokens from "./Templates.tokens";
import { hasAccessTo } from "layouts/Config";
import strings from "global/constants/StringConstants";
import Unauthorized from "screens/Shared/Unauthorized";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import viewpaths from "global/constants/ViewPathConstants";
import history from "utils/history";
import {
  selectScreen,
  setIsRequest,
  setReduxSignRequest,
} from "redux/appSlice";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import { CreateWebFormModal } from "screens/WebForms/CreateWebFormModal";
import EmptyState from "global/components/EmptyState/EmptyState";
import { tourAttr, tourAttrIf } from "tour";
import FeatureGateBlock from "global/components/FeatureGateBlock/FeatureGateBlock";
import { useLocation } from "react-router-dom";
import TemplateTabs, {
  TemplatesTab,
} from "./TemplateLibrary/components/TemplateTabs";
import TemplateLibrary from "./TemplateLibrary/TemplateLibrary";
import TemplateBreadcrumb from "./components/TemplateBreadcrumb";

type View = "grid" | "list";

interface customProps {
  isShowCreateBtn?: boolean;
  isShowMoreIcon?: boolean;
  isUseTemplate?: boolean;
  handleCloseTemplateModal?: any;
  isMobile?: boolean;
  onTemplateSelect?: (template: any) => void;
}

const styles = templatesStyles;
const { footer } = templatesTokens;

const MyTemplates = (props: customProps) => {
  useTitle(title.TEMPLATES);
  const [data, setData] = useState<FolderItem[]>([]);
  const [dataCount, setDataCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false);
  const [isActionLoading, setIsActionLoading] = useState<boolean>(false);
  const [parentId, setParentId] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [perPageTemplates] = useState<number>(10);
  const observerRef = useRef<HTMLDivElement | null>(null);
  const [sortConfig] = useState<SortingConfig[]>([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [dialogType, setDialogType] = useState<any>("");
  const [newFolderName, setNewFolderName] = useState<string>("");
  const [folderNameError, setFolderNameError] = useState<string>("");
  const [view, setView] = useState<View>("grid");
  const [selectedFolder, setSelectedFolder] = useState<any | null>(null);
  const [destFolder, setDestFolder] = useState<any | null>();
  const [folderTree, setFolderTree] = useState<any[]>([]);
  const [isWebFormModalOpen, setIsWebFormModalOpen] = useState(false);
  const [path, setPath] = useState<Path[]>([{ id: 0, name: "Home" }]);
  const [searchText, setSearchText] = useState<string>("");
  const [searchResults, setSearchResults] = useState<FolderItem[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const searchAnchorRef = useRef<HTMLDivElement | null>(null);
  const searchAbortRef = useRef<AbortController | null>(null);
  const isMobile = useAppSelector(selectScreen);
  const theme = useTheme();
  /**
   * Phone column of the reference. Below `sm` every item is drawn as one
   * compact row whichever way the (hidden) view toggle is set, so the split
   * has to be a real breakpoint rather than the redux `isMobile` flag — that
   * one is only recomputed on a window resize, so it is wrong whenever the
   * viewport starts small.
   */
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const { currentPlan, accountDetails, setAccountDetails } =
    useContext(AccountContext);

  // The account's live usage (`/secure/billing/fetch/planLimit`) is the one
  // that carries `current`; the plan's own `limits` only has the cap, so it is
  // just the fallback while account details have not loaded.
  const tplLimitEntry =
    accountDetails?.appPlanLimitList?.find((l) => l.entity === "template") ??
    currentPlan?.limits?.find((l) => l.entity === "template");
  const isAtTemplateLimit =
    !!tplLimitEntry && (tplLimitEntry.current ?? 0) >= tplLimitEntry.limit;

  const dispatch = useAppDispatch();

  /*
    Tab and open library template live in the URL (`?tab=library&template=…`)
    so a refresh or the browser's back button lands in the same place. The
    "choose a template" picker (`isUseTemplate`) has no library tab.
  */
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const activeTab: TemplatesTab =
    !props.isUseTemplate && query.get("tab") === "library" ? "library" : "mine";
  const libraryDetailId =
    activeTab === "library" ? query.get("template") : null;

  const hasTemplateFetchPermission = hasAccessTo(
    strings.resources.template,
    strings.fetchPermission,
  );
  const templateUseState = useFeatureState(FeatureKeys.TEMPLATE_USE);
  const templateAvailable =
    templateUseState.status === strings.FEATURE_STATUS_AVAILABLE;
  const templateFoldersState = useFeatureState(FeatureKeys.TEMPLATE_FOLDERS);
  const templateFoldersEnabled =
    templateFoldersState.status === strings.FEATURE_STATUS_AVAILABLE;
  const templateFoldersDisabledTooltip =
    templateFoldersState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? (templateFoldersState.disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : templateFoldersState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : undefined;

  useEffect(() => {
    hasTemplateFetchPermission && templateAvailable && fetchFoldersData();
  }, []);

  useEffect(() => {
    if (pageNumber === 1 || !templateAvailable) return;
    fetchFoldersData();
  }, [pageNumber]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading) {
          const maxPages = Math.ceil(dataCount / perPageTemplates);
          if (pageNumber < maxPages) {
            setPageNumber((prev) => prev + 1);
          }
        }
      },
      { threshold: 1.0 },
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [isLoading, dataCount, perPageTemplates, pageNumber]);

  useEffect(() => {
    hasTemplateFetchPermission && templateAvailable && fetchFolderTreeData();
  }, [parentId]);

  const folderIndex = useMemo(() => {
    const idx: Record<number, Folder> = {};
    const walk = (f: Folder) => {
      idx[f.folderId] = f;
      (f.subFolders || []).forEach(walk);
    };
    (folderTree || []).forEach(walk);
    return idx;
  }, [folderTree]);

  useEffect(() => {
    return () => {
      searchAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const trimmed = searchText.trim();
    if (!trimmed) {
      searchAbortRef.current?.abort();
      searchAbortRef.current = null;
      setSearchResults([]);
      setIsSearching(false);
      setSearchOpen(false);
      return;
    }
    const timer = setTimeout(async () => {
      searchAbortRef.current?.abort();
      const controller = new AbortController();
      searchAbortRef.current = controller;
      setIsSearching(true);
      try {
        const response = await searchTemplatesAndFolders(
          trimmed,
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setSearchResults(Array.isArray(response) ? response : []);
        setSearchOpen(true);
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          setSearchResults([]);
          setSearchOpen(true);
        }
      } finally {
        if (!controller.signal.aborted) setIsSearching(false);
      }
    }, strings.SEARCH_TIME_OUT);
    return () => clearTimeout(timer);
  }, [searchText]);

  const buildPathToFolder = (targetFolderId: number): Path[] => {
    const home: Path = { id: 0, name: "Home" };
    if (!targetFolderId) return [home];
    const chain: number[] = [];
    let cur: Folder | undefined = folderIndex[targetFolderId];
    while (cur) {
      chain.unshift(cur.folderId);
      cur = cur.parentId ? folderIndex[cur.parentId] : undefined;
    }
    return [
      home,
      ...chain.map((id) => ({
        id,
        name: folderIndex[id]?.name ?? "…",
      })),
    ];
  };

  const resolveAncestorCrumbs = (item: FolderItem): string[] => {
    const ids = (item.path ?? "")
      .split(".")
      .map(Number)
      .filter((n) => !Number.isNaN(n));
    const ancestorIds = ids.slice(0, -1);
    return ["Home", ...ancestorIds.map((id) => folderIndex[id]?.name ?? "…")];
  };

  const handleSearchSelect = async (item: FolderItem) => {
    setSearchOpen(false);
    const isFolder = item.type === strings.folder;
    if (isFolder) {
      const newPath = [
        ...buildPathToFolder(item.parentId || 0),
        { id: item.autoGeneratedId, name: item.templateName ?? "" },
      ];
      await navigateToFolder(item.autoGeneratedId, newPath);
    } else {
      // Clicking a template result USES it (starts a new request from it),
      // matching the grid / list card click — "Update template" stays available
      // in the row's ⋮ menu for anyone who wants to edit the template instead.
      handleUseTemplate(item);
    }
  };

  const navigateToFolder = async (targetParentId: number, newPath: Path[]) => {
    setData([]);
    setPath(newPath);
    setParentId(targetParentId);
    setPageNumber(1);

    setIsLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 0));

    try {
      const [response, count] = await Promise.all([
        fetchFolderData(1, targetParentId, sortConfig),
        fetchFolderDataCount(targetParentId),
      ]);
      setData(response);
      setDataCount(count);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFolderClick = async (item: any) => {
    const newPath = [
      ...path,
      { id: item.autoGeneratedId, name: item.templateName },
    ];
    await navigateToFolder(item.autoGeneratedId, newPath);
  };

  const handleBreadcrumbClick = async (index: number) => {
    if (index === path.length - 1) return;

    const newPath = path.slice(0, index + 1);
    const targetParentId = newPath[newPath.length - 1].id;

    await navigateToFolder(targetParentId, newPath);
  };

  const handleHomeClick = async () => {
    const newPath = [{ id: 0, name: "Home" }];
    await navigateToFolder(0, newPath);
  };

  const fetchFoldersData = async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      const [response, count] = await Promise.all([
        fetchFolderData(pageNumber, parentId, sortConfig),
        fetchFolderDataCount(parentId),
      ]);
      setData((prev) => (pageNumber === 1 ? response : [...prev, ...response]));
      setDataCount(count);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  };

  const fetchFolderTreeData = async () => {
    try {
      const foldersTree = await fetchFolderTree();
      setFolderTree(foldersTree);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const moveFolders = async (folderId: number, destinationFolder: number) => {
    try {
      setOpenDialog(false);
      setIsActionLoading(true);
      await moveFolder(folderId, destinationFolder);
      setNewFolderName("");
      setDestFolder(null);
      await navigateToFolder(parentId, path);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsActionLoading(false);
    }
  };

  const deleteFolder = async (folderId: number) => {
    try {
      setOpenDialog(false);
      setIsActionLoading(true);
      await deleteTemplate(folderId);
      await navigateToFolder(parentId, path);
    } catch (error: any) {
      if (error?.code === 406) {
        setDialogType && setDialogType(strings.deleteAnyway);
        setOpenDialog(true);
        setIsActionLoading(true);
      }
    } finally {
      setIsActionLoading(false);
    }
  };

  const deleteAnyway = async (folderId: number) => {
    try {
      setIsActionLoading(true);
      setOpenDialog(false);
      await deleteAnywayFolder(folderId);
      await navigateToFolder(parentId, path);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsActionLoading(false);
    }
  };

  const createNewFolderFunction = async (parentFolderId: number) => {
    const trimmedName = newFolderName.trim();
    if (!trimmedName) {
      setFolderNameError("Please enter folder name");
      return;
    }
    if (/[^a-zA-Z0-9_\- ]/.test(trimmedName)) {
      setFolderNameError("Only letters, numbers, spaces, - and _ are allowed");
      return;
    }
    setFolderNameError("");
    try {
      setOpenDialog(false);
      setIsActionLoading(true);
      const folderRequest: SignRequest = {
        ...initialSignRequest,
        templateName: trimmedName,
        parentId: parentFolderId,
        type: "folder",
      };
      await createNewFolder(true, folderRequest);
      setNewFolderName("");
      fetchFolderTreeData();
      openSuccessNotification("Folder created successfully!");
      await navigateToFolder(parentId, path);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsActionLoading(false);
    }
  };

  const renameFolderFunction = async () => {
    const trimmedName = newFolderName.trim();
    if (!trimmedName) {
      setFolderNameError("Please enter folder name");
      return;
    }
    if (/[^a-zA-Z0-9_\- ]/.test(trimmedName)) {
      setFolderNameError("Only letters, numbers, spaces, - and _ are allowed");
      return;
    }
    if (trimmedName === selectedFolder?.templateName) {
      setFolderNameError("Please enter a different name");
      return;
    }
    setFolderNameError("");
    try {
      setOpenDialog(false);
      setIsActionLoading(true);
      await renameFolder(selectedFolder.autoGeneratedId, trimmedName);
      setNewFolderName("");
      setSelectedFolder(null);
      await navigateToFolder(parentId, path);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setFolderNameError("");
    setNewFolderName("");
    setDestFolder(null);
  };

  const handleOpenFolderClick = () => {
    setDialogType(strings.createNewFolder);
    setOpenDialog(true);
    setNewFolderName("");
  };

  const handleTemplateClick = () => {
    dispatch(setIsRequest(false));
    dispatch(setReduxSignRequest(undefined));
    // Carry the folder the user is standing in through to the builder, so the
    // template is saved under it instead of the root. Router state is the only
    // channel that survives: the builder clears the redux request on mount
    // before it reads the parent. `parentId` 0 is the root.
    history.push({
      pathname: viewpaths.createTemplateViewPath,
      state: { newTemplate: true, parentId },
    });
  };

  const handleUpdateTemplate = () => {
    dispatch(setIsRequest(false));
    const template = {
      ...selectedFolder,
      files:
        selectedFolder.files?.map((file: any) => ({
          ...file,
          unsignedDoc: file.unsignedPdf || file.unsignedDoc,
          unsignedDocUrl: file.unsignedPdfUrl || file.unsignedDocUrl,
        })) || [],
    };
    dispatch(setReduxSignRequest(template));
    history.push({
      pathname: viewpaths.updateTemplateViewPath,
      state: template,
    });
  };

  const handleUseTemplate = (item?: SignRequest) => {
    dispatch(setIsRequest(false));
    const selectedItem = item || selectedFolder;
    const templateStoreId = selectedItem?.id;
    // `parentId` / `path` on a template point into the TEMPLATE folder tree, so
    // they must not ride along into the sign request created from it — that
    // would file the request against a folder id that does not exist in the
    // sign-request tree. New requests always start at the sign-request root.
    const {
      parentId: templateParentId,
      path: templatePath,
      ...templateSource
    } = (selectedItem ?? {}) as SignRequest & { path?: string };
    const template = {
      ...templateSource,
      parentId: 0,
      templateId:
        templateStoreId != null && String(templateStoreId).trim() !== ""
          ? String(templateStoreId)
          : null,
      files:
        selectedItem?.files?.map((file: any) => ({
          ...file,
          unsignedDoc: file.unsignedPdf || file.unsignedDoc,
          unsignedDocUrl: file.unsignedPdfUrl || file.unsignedDocUrl,
        })) || [],
    };

    if (props.onTemplateSelect) {
      props.onTemplateSelect(template);
      return;
    }

    dispatch(setReduxSignRequest(template));
    dispatch(setIsRequest(true));
    history.push({
      pathname: viewpaths.useTemplateViewPath,
      state: template,
    });
  };

  /*
    A plan-blocked page shows its content disabled, so the create actions come
    off with the feature — not just the permission.
  */
  const canCreate =
    hasAccessTo(strings.resources.template, strings.addPermission) &&
    templateAvailable;

  const showTabs = !props.isUseTemplate;

  const setLibraryUrl = (search: string) =>
    history.push({ pathname: location.pathname, search });

  const handleTabChange = (tab: TemplatesTab) => {
    if (tab === activeTab && !libraryDetailId) return;
    setLibraryUrl(tab === "library" ? "?tab=library" : "");
  };

  /**
   * A library import adds a template at the root of My Templates and uses one
   * of the plan's template slots. The list and the account's usage are
   * reloaded straight away — My
   * Templates stays mounted behind the library tab — so the list, its count
   * and the template limit are right however the user comes back (tab,
   * "Go to My templates", browser back, or after "Use template").
   */
  const handleLibraryImported = () => {
    handleHomeClick();
    // Same permission App checks before it loads account details at all.
    if (hasAccessTo(strings.resources.billing, strings.fetchPermission)) {
      fetchAccountDetails()
        .then((details) => details && setAccountDetails(details as AccountDetails))
        .catch(() => {
          // The backend still enforces the limit; a stale count only affects the hint.
        });
    }
  };

  const libraryPermissions = {
    canUse: hasAccessTo(strings.resources.sign, strings.sendPermission),
    canImport: canCreate,
    importBlockedReason: isAtTemplateLimit
      ? "You've used all template slots in your plan. Upgrade to add more."
      : undefined,
  };

  /**
   * Full-card placeholder shown at the root of an account that has nothing
   * saved yet — the design's "fresh account" state.
   */
  const FreshState = () => (
    // No wrapper card: the block draws its own border (`framed`), so an empty
    // account gets one box instead of a tinted panel inside an empty card.
    <Box {...tourAttr("templates.content")}>
      <EmptyState
        framed
        icon="template"
        title="No templates yet"
        body="Turn any document you send repeatedly into a template — set up fields once and reuse it for every new request."
        primaryAction={
          props.isShowCreateBtn !== false && canCreate
            ? {
                label: "Create your first template",
                onClick: handleTemplateClick,
                disabled: isLoading || isAtTemplateLimit,
              }
            : undefined
        }
        secondaryAction={
          props.isShowCreateBtn !== false && canCreate
            ? {
                label: "Create folder",
                onClick: handleOpenFolderClick,
                disabled: isLoading || templateFoldersEnabled === false,
              }
            : undefined
        }
      />
    </Box>
  );

  /** Compact in-place message for an empty folder. */
  const FolderEmptyState = () => (
    <EmptyState
      size="medium"
      icon="folder"
      title="This folder is empty"
      body="Add a template or a subfolder to get started."
    />
  );

  /**
   * A brand-new account (root folder, nothing saved) gets the design's
   * full-card placeholder instead of the toolbar / grid, so the trail and the
   * header actions come off with it.
   */
  const isFreshAccount = !isLoading && parentId === 0 && data.length === 0;

  const getContent = () => {
    if (!isLoading && data.length === 0) {
      return isFreshAccount ? <FreshState /> : <FolderEmptyState />;
    }

    // Phone: the reference draws one compact row per item and hides the view
    // toggle, so grid / list have nothing to switch between down here.
    if (isPhone) {
      return (
        <Box aria-busy={isLoading} sx={styles.mobileList}>
          {data.map((item, idx) => (
            <TemplateGridView
              key={item.id ?? `item-${idx}`}
              compact
              item={item}
              setOpenDialog={setOpenDialog}
              setDialogType={setDialogType}
              selectedFolder={selectedFolder}
              setSelectedFolder={setSelectedFolder}
              setNewFolderName={setNewFolderName}
              setFolderNameError={setFolderNameError}
              handleFolderClick={handleFolderClick}
              handleUpdateTemplate={handleUpdateTemplate}
              handleUseTemplate={handleUseTemplate}
              handleCreateWebForm={() => setIsWebFormModalOpen(true)}
              isLoading={isActionLoading}
              isShowMoreIcon={props.isShowMoreIcon}
              isUseTemplate={props.isUseTemplate}
              dispatch={dispatch}
              handleCloseTemplateModal={props?.handleCloseTemplateModal}
            />
          ))}
          {isLoading && renderMobileSkeletons()}
        </Box>
      );
    }

    if (view === "grid") {
      return (
        <Box
          {...tourAttr("templates.content")}
          aria-busy={isLoading}
          sx={styles.grid}
        >
          {data.map((item, idx) => (
            <TemplateGridView
              key={item.id ?? `item-${idx}`}
              item={item}
              setOpenDialog={setOpenDialog}
              setDialogType={setDialogType}
              selectedFolder={selectedFolder}
              setSelectedFolder={setSelectedFolder}
              setNewFolderName={setNewFolderName}
              setFolderNameError={setFolderNameError}
              handleFolderClick={handleFolderClick}
              handleUpdateTemplate={handleUpdateTemplate}
              handleUseTemplate={handleUseTemplate}
              handleCreateWebForm={() => setIsWebFormModalOpen(true)}
              isLoading={isActionLoading}
              isShowMoreIcon={props.isShowMoreIcon}
              isUseTemplate={props.isUseTemplate}
              dispatch={dispatch}
              handleCloseTemplateModal={props?.handleCloseTemplateModal}
            />
          ))}
          {isLoading && renderGridSkeletons()}
        </Box>
      );
    }

    return (
      <Box
        {...tourAttr("templates.content")}
        aria-busy={isLoading}
        sx={styles.listCard}
      >
        <Box sx={styles.listHead}>
          <Box>Name</Box>
          <Box>Type</Box>
          <Box>Created by</Box>
          <Box>Created</Box>
          <Box />
        </Box>
        {data.map((item, idx) => (
          <ItemListRow
            key={item.id ?? `item-${idx}`}
            item={item}
            setOpenDialog={setOpenDialog}
            setDialogType={setDialogType}
            selectedFolder={selectedFolder}
            setSelectedFolder={setSelectedFolder}
            setNewFolderName={setNewFolderName}
            setFolderNameError={setFolderNameError}
            handleFolderClick={handleFolderClick}
            handleUpdateTemplate={handleUpdateTemplate}
            handleUseTemplate={handleUseTemplate}
            handleCreateWebForm={() => setIsWebFormModalOpen(true)}
            isLoading={isActionLoading}
            handleCloseTemplateModal={props?.handleCloseTemplateModal}
            dispatch={dispatch}
            isUseTemplate={props.isUseTemplate}
            isShowMoreIcon={props.isShowMoreIcon}
            isMobile={isMobile}
          />
        ))}
        {isLoading && renderListSkeletons(isMobile)}
      </Box>
    );
  };

  const getPage = () => {
    if (!templateAvailable) {
      return (
        <FeatureGateBlock
          variant="full"
          featureName="Templates"
          status={
            templateUseState.status as "not-in-plan" | "admin-disabled"
          }
          disabledMessage={(templateUseState as any).disabledMessage}
        />
      );
    }
    return (
      <>
        {isActionLoading && (
          <Box
            role="status"
            aria-live="polite"
            aria-label="Loading"
            sx={{
              // `inset: 0` rather than `100vw/100vh`: viewport units include
              // the scrollbar gutter, so a 100vw overlay is wider than the area
              // it covers.
              position: "fixed",
              inset: 0,
              bgcolor: "rgba(255,255,255,0.5)",
              zIndex: 2000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box>
              <Box display="flex" justifyContent="center" alignItems="center">
                <Box sx={{ color: "primary.main" }}>
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    aria-hidden="true"
                  >
                    <circle
                      cx="20"
                      cy="20"
                      r="18"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                      strokeDasharray="90"
                      strokeDashoffset="60"
                    >
                      <animateTransform
                        attributeName="transform"
                        type="rotate"
                        from="0 20 20"
                        to="360 20 20"
                        dur="1s"
                        repeatCount="indefinite"
                      />
                    </circle>
                  </svg>
                </Box>
              </Box>
            </Box>
            <Box
              component="span"
              sx={{
                position: "absolute",
                width: "1px",
                height: "1px",
                p: 0,
                m: "-1px",
                overflow: "hidden",
                clip: "rect(0 0 0 0)",
                whiteSpace: "nowrap",
                border: 0,
              }}
            >
              Loading
            </Box>
          </Box>
        )}
        <Box
          sx={styles.page(!!props.isUseTemplate)}
          {...tourAttrIf(hasLoadedOnce && !isLoading, "templates.ready")}
        >
          {!libraryDetailId && (
            <TemplateHeader
              onViewChange={setView}
              view={view}
              path={path}
              onBreadcrumbClick={handleBreadcrumbClick}
              onHomeClick={handleHomeClick}
              isLoading={isLoading}
              setOpenDialog={setOpenDialog}
              setDialogType={setDialogType}
              setNewFolderName={setNewFolderName}
              handleOpenFolderClick={handleOpenFolderClick}
              handleTemplateClick={handleTemplateClick}
              isShowCreateBtn={props.isShowCreateBtn ?? true}
              isAtTemplateLimit={isAtTemplateLimit}
              isMobile={isMobile}
              foldersEnabled={templateFoldersEnabled}
              foldersDisabledTooltip={templateFoldersDisabledTooltip}
              // With tabs, the trail moves below them (rendered further down).
              showBreadcrumb={!showTabs && !isFreshAccount}
              showViewToggle={activeTab === "mine"}
              hideTitle={props.isUseTemplate}
            />
          )}

          {showTabs && !libraryDetailId && (
            <TemplateTabs value={activeTab} onChange={handleTabChange} />
          )}

          {activeTab === "library" && (
            <TemplateLibrary
              detailId={libraryDetailId}
              onOpenDetail={(id) =>
                setLibraryUrl(`?tab=library&template=${encodeURIComponent(id)}`)
              }
              onCloseDetail={() => setLibraryUrl("?tab=library")}
              onGoToMyTemplates={() => handleTabChange("mine")}
              onUseTemplate={handleUseTemplate}
              onImported={handleLibraryImported}
              permissions={libraryPermissions}
            />
          )}

          {/*
            My Templates stays mounted while the library tab is open, so its
            folder, search and scroll position survive a round trip.
            `contents` keeps its children in the page's flex gap.
          */}
          <Box sx={{ display: activeTab === "mine" ? "contents" : "none" }}>
            {showTabs && !isFreshAccount && (
              <TemplateBreadcrumb
                path={path}
                onBreadcrumbClick={handleBreadcrumbClick}
                onHomeClick={handleHomeClick}
                isLoading={isLoading}
              />
            )}

            {!isFreshAccount && (
              <TemplatesToolbar
                searchText={searchText}
                onSearchChange={setSearchText}
                searchAnchorRef={searchAnchorRef}
                searchOpen={searchOpen}
                setSearchOpen={setSearchOpen}
                isSearching={isSearching}
                searchResults={searchResults ?? []}
                resolveAncestorCrumbs={resolveAncestorCrumbs}
                onSearchSelect={handleSearchSelect}
                disabled={isLoading}
                renderItemActions={(item) => (
                  <TemplateItemMenu
                    item={item}
                    setOpenDialog={setOpenDialog}
                    setDialogType={setDialogType}
                    setSelectedFolder={setSelectedFolder}
                    setNewFolderName={setNewFolderName}
                    setFolderNameError={setFolderNameError}
                    handleUpdateTemplate={handleUpdateTemplate}
                    handleUseTemplate={handleUseTemplate}
                    handleCreateWebForm={() => setIsWebFormModalOpen(true)}
                    isShowMoreIcon={props.isShowMoreIcon}
                    onActionStart={() => setSearchOpen(false)}
                  />
                )}
              />
            )}
            {getContent()}
            {data.length > 0 && (
              <Box role="status" aria-live="polite" sx={styles.footer}>
                {isLoading && (
                  <Box sx={styles.footerLoading}>
                    <CircularProgress
                      size={footer.spinnerSize}
                      thickness={footer.spinnerThickness}
                      sx={styles.footerSpinner}
                    />
                    Loading more…
                  </Box>
                )}
                {!isLoading && data.length >= dataCount && (
                  <Typography component="div" sx={styles.footerAtEnd}>
                    All {dataCount} items loaded
                  </Typography>
                )}
                <Typography component="div" sx={styles.footerShown}>
                  Showing {data.length} of {dataCount} items
                </Typography>
              </Box>
            )}
            <div ref={observerRef}></div>
          </Box>
        </Box>
        <CustomDialogComponent
          openDialog={openDialog}
          handleDialogClose={handleDialogClose}
          dialogType={dialogType}
          newFolderName={newFolderName}
          setNewFolderName={setNewFolderName}
          folderNameError={folderNameError}
          setFolderNameError={setFolderNameError}
          moveFolders={moveFolders}
          createNewFolderFunction={createNewFolderFunction}
          renameFolderFunction={renameFolderFunction}
          deleteFolder={deleteFolder}
          deleteAnyway={deleteAnyway}
          selectedFolder={selectedFolder}
          setSelectedFolder={setSelectedFolder}
          setDestFolder={setDestFolder}
          destFolder={destFolder}
          folderTree={folderTree}
          parentId={parentId}
        />
        <CreateWebFormModal
          open={isWebFormModalOpen}
          onClose={() => setIsWebFormModalOpen(false)}
          initialTemplate={selectedFolder}
          onComplete={(data) => {
            let signerId =
              data.signers && data.signers.length > 0 ? data.signers[0] : 1;
            history.push(
              `${viewpaths.webFormsViewPath}/builder/new?templateId=${data.templateId}&signerId=${signerId}&name=${encodeURIComponent(data.name)}`,
            );
          }}
        />
      </>
    );
  };

  // Embedded in the "choose a template" modal (AppModal already supplies the
  // dialog's own gutter/scroll chrome) rather than the standalone /templates
  // page: skip the page layout's own top margin + Container gutters, or they
  // stack on top of the modal's padding and the compact `page()` style below.
  if (props.isUseTemplate) {
    return hasTemplateFetchPermission ? (
      getPage()
    ) : (
      <Unauthorized permissionDenied={hasTemplateFetchPermission} />
    );
  }

  return (
    <>
      {hasTemplateFetchPermission ? (
        <CenteredAuthenticatedLayout>{getPage()}</CenteredAuthenticatedLayout>
      ) : (
        <Unauthorized permissionDenied={hasTemplateFetchPermission} />
      )}
    </>
  );
};

export default MyTemplates;
